<template>
  <div v-if="hasError" class="error-boundary">
    <div class="error-container">
      <div class="error-icon">
        <AlertTriangle class="h-12 w-12 text-red-500" />
      </div>
      <h2 class="error-title">Something went wrong</h2>
      <p class="error-message">{{ errorMessage }}</p>
      <div class="error-actions">
        <Button @click="retry" variant="outline" class="btn-enhanced">
          <RefreshCw class="h-4 w-4 mr-2" />
          Try Again
        </Button>
        <Button @click="reportError" variant="ghost" class="btn-enhanced">
          <Bug class="h-4 w-4 mr-2" />
          Report Issue
        </Button>
      </div>
      <details class="error-details">
        <summary class="error-details-summary">Technical Details</summary>
        <pre class="error-stack">{{ errorStack }}</pre>
      </details>
    </div>
  </div>
  <slot v-else />
</template>

<script>
import { ref, onErrorCaptured, provide } from 'vue'
import Button from './ui/button.vue'
import { AlertTriangle, RefreshCw, Bug } from 'lucide-vue-next'

export default {
  name: 'ErrorBoundary',
  components: {
    Button,
    AlertTriangle,
    RefreshCw,
    Bug
  },
  setup() {
    const hasError = ref(false)
    const errorMessage = ref('')
    const errorStack = ref('')

    onErrorCaptured((error, instance, info) => {
      hasError.value = true
      errorMessage.value = error.message || 'An unexpected error occurred'
      errorStack.value = `${error.stack}\n\nComponent Info: ${info}`
      
      console.error('Error Boundary caught an error:', error, info)
      
      // You could also send this to an error reporting service
      // errorReportingService.captureException(error, { extra: { info } })
      
      return false // Prevent the error from propagating further
    })

    const retry = () => {
      hasError.value = false
      errorMessage.value = ''
      errorStack.value = ''
    }

    const reportError = () => {
      // In a real app, this would open a bug report form or send to error tracking service
      console.log('Error reported:', {
        message: errorMessage.value,
        stack: errorStack.value,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      })
      
      // For now, just show an alert
      alert('Error has been reported. Thank you for helping us improve!')
    }

    // Provide error handling methods to child components
    provide('errorBoundary', {
      reportError: (error) => {
        hasError.value = true
        errorMessage.value = error.message || 'An error occurred'
        errorStack.value = error.stack || ''
      }
    })

    return {
      hasError,
      errorMessage,
      errorStack,
      retry,
      reportError
    }
  }
}
</script>

<style scoped>
.error-boundary {
  @apply min-h-screen flex items-center justify-center bg-gray-50 p-4;
}

.error-container {
  @apply max-w-md w-full bg-white rounded-xl shadow-enhanced-xl p-8 text-center animate-bounce-in;
}

.error-icon {
  @apply mb-4 animate-wiggle;
}

.error-title {
  @apply text-xl font-semibold text-gray-900 mb-2;
}

.error-message {
  @apply text-gray-600 mb-6;
}

.error-actions {
  @apply flex gap-3 justify-center mb-6;
}

.error-details {
  @apply text-left;
}

.error-details-summary {
  @apply cursor-pointer text-sm text-gray-500 hover:text-gray-700 mb-2;
}

.error-stack {
  @apply text-xs text-gray-400 bg-gray-100 p-3 rounded overflow-auto max-h-32;
}
</style>
